import { useCallback, useMemo, useRef } from 'react'

import { FormConfig } from '@workwave-tidal/core/form'

import { form } from './formState'
import type { ExecutionEventsFormValues, Props } from './typings'
import { computeDirtyFields, getExecutionEventsChanges, getFormInitialValues } from './utils'

import { Form } from './components/Form'
import { Footer } from './components/Footer'
import { Header } from './components/Header'

export function EditExecutionEvents(props: Props) {
  const {
    tab,
    order,
    onCancel,
    planLocked,
    workingDayStartSec,
    updateExecutionEvent,
    updateExecutionEventPodMarkers,
  } = props

  const { id: extOrderId } = order
  const initialValues = useRef(getFormInitialValues(order))

  const handleOnSubmit = useCallback(
    async (formData: ExecutionEventsFormValues) => {
      const dirtyFields = computeDirtyFields(initialValues.current, formData)

      if (dirtyFields.length === 0) {
        onCancel()
        return
      }

      const changes = getExecutionEventsChanges(
        extOrderId,
        formData,
        initialValues.current,
        dirtyFields,
        workingDayStartSec,
      )

      await updateExecutionEvent(changes)
    },
    [updateExecutionEvent, workingDayStartSec, extOrderId, onCancel],
  )

  const config = useMemo<FormConfig<ExecutionEventsFormValues>>(
    () => ({
      id: 'execution-events-form',
      initialValues: initialValues.current,
    }),
    [initialValues],
  )

  return (
    <form.Form
      testId="orderEditExecutionEvents"
      header={<Header order={order} tab={tab} onClose={onCancel} />}
      footer={<Footer planLocked={planLocked} handleCancel={onCancel} />}
      onSubmit={handleOnSubmit}
      config={config}
      rightBorder
      requireTransaction
      stopEditingOnUnmount
      applyPendingFitMapOnMount={true}
      disableScroll
    >
      <Form
        {...props}
        initialValues={initialValues.current}
        onTogglePod={updateExecutionEventPodMarkers}
      />
    </form.Form>
  )
}
